import { Button, Icon, List, Popconfirm, Spin, Table } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import moment, { Moment } from 'moment';
import React from 'react';
import { AbsencePeriod, Replacer } from '../../../../core-module/services/absence/types';
import { AbsenceFormContainer } from './AbsenceFormContainer';
import { ConfirmationStatusBadge } from './ConfirmationStatusBadge';
import { getAbsenceReasonDisplayName, isBeforeAllowedAbsenceDate } from './helpers';
import { ReplacementFormContainer } from './ReplacementFormContainer';
import { EditState } from './types';

interface Props {
  driverId: number;
  loading: boolean;
  absencePeriods: AbsencePeriod[];
  replacers: Replacer[];
  soonestAllowedAbsenceDate: Moment;
  editState: EditState;
  onEditAbsencePeriod: (absencePeriod?: AbsencePeriod) => void;
  onCloseEditing: () => void;
  onRemoveReplacement: (absencePeriodId: number, replacementId: number) => void;
  onCloseReplacementForm: () => void;
}

const AbsenceOverviewComponent: React.FC<Props> = ({
  driverId,
  loading,
  absencePeriods,
  replacers,
  soonestAllowedAbsenceDate,
  editState,
  onEditAbsencePeriod,
  onCloseEditing,
  onRemoveReplacement,
  onCloseReplacementForm
}) => {
  const columns: ColumnProps<AbsencePeriod>[] = [
    {
      title: 'Afwezig vanaf',
      key: 'from',
      width: '8%',
      render: (_, item) => <span>{moment(item.from).format('dddd D MMMM YYYY')}</span>
    },
    {
      title: 'Afwezig t/m',
      key: 'to',
      width: '8%',
      render: (_, item) => <span>{moment(item.to).format('dddd D MMMM YYYY')}</span>
    },
    {
      title: 'Reden',
      key: 'reason',
      width: '10%',
      render: (_, item) => <span>{getAbsenceReasonDisplayName(item.reason)}</span>
    },
    {
      title: 'Vervanging',
      key: 'replacement',
      render: (_, item) => (
        <List
          itemLayout='horizontal'
          size='small'
          locale={{ emptyText: 'Nog geen vervanging ingevoerd' }}
          dataSource={item.replacements}
          renderItem={(replacement) => (
            <List.Item>
              <span>
                {moment(replacement.date).format('dddd D MMMM YYYY')}:{' '}
                {replacement.type === 'Acquaintance' && (
                  <span>
                    Kennis ({replacement.acquaintanceName}, {replacement.acquaintancePhoneNumber})
                  </span>
                )}
                {replacement.type === 'Driver' && (
                  <>
                    <span>{replacement.replacementDriverName}&nbsp;</span>
                    <ConfirmationStatusBadge confirmationStatus={replacement.confirmationStatus} />
                  </>
                )}
                {!isBeforeAllowedAbsenceDate(
                  moment(replacement.date),
                  soonestAllowedAbsenceDate
                ) && (
                  <Popconfirm
                    title={
                      <>
                        <div>Weet je zeker dat je de vervanging wilt verwijderen? </div>
                        {replacement.type === 'Driver' && (
                          <div>De bezorger zal hier een bericht over ontvangen.</div>
                        )}
                      </>
                    }
                    onConfirm={() => onRemoveReplacement(item.id, replacement.id)}
                  >
                    &nbsp;
                    <Icon type='close' style={{ color: 'red' }} title='Vervanging verwijderen' />
                  </Popconfirm>
                )}
              </span>
            </List.Item>
          )}
          footer={
            <ReplacementFormContainer
              driverId={driverId}
              absencePeriod={item}
              soonestAllowedAbsenceDate={soonestAllowedAbsenceDate}
              replacers={replacers}
              onClose={onCloseReplacementForm}
            />
          }
        />
      )
    },
    {
      title: 'Acties',
      key: 'actions',
      width: '100px',
      render: (_, item) => (
        <Button type='primary' icon='edit' onClick={() => onEditAbsencePeriod(item)} />
      )
    }
  ];

  const visiblityFilter = (absencePeriod: AbsencePeriod) => {
    return moment(absencePeriod.to).isSameOrAfter(moment(), 'day');
  };

  const visibleAbsencePeriods = absencePeriods.filter(visiblityFilter);

  return (
    <Spin spinning={loading} delay={500}>
      <Button type='primary' onClick={() => onEditAbsencePeriod()} style={{ marginBottom: 10 }}>
        Afwezigheidsperiode toevoegen
      </Button>
      <Table
        rowKey={(row, index) => index.toString()}
        dataSource={visibleAbsencePeriods}
        locale={{ emptyText: 'Geen data beschikbaar' }}
        bordered={true}
        pagination={false}
        columns={columns}
      />

      {editState.editing && (
        <AbsenceFormContainer
          driverId={driverId}
          visible={editState.editing}
          onClose={onCloseEditing}
          absencePeriod={editState.absencePeriod}
          soonestAllowedAbsenceDate={soonestAllowedAbsenceDate}
        />
      )}
    </Spin>
  );
};

export { AbsenceOverviewComponent };
